import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import ConsumptionProgressBar from './ConsumptionProgressBar';
import ApiClient from '../../utils/apiClient';
import {
  getDailyCacheKey,
  getCachedData,
  setDailyCachedData,
  createCacheKeyFromParams
} from '../../utils/cacheUtils';

interface PeakData {
  message: string;
  analysis_type: string;
  period_days: number;
  consumption_type: string;
  consumption_type_id: number;
  peak_hours?: string;
  // Additional fields for electricity peak hours analysis
  usage_category?: string;
  color_indicator?: string;
  percentage_value?: number;
  typical_range?: {
    low: number;
    high: number;
  };
  // Additional fields for CO2 peak period analysis
  start_time?: string;
  end_time?: string;
  period_length?: number;
}

interface PeakConsumptionProps {
  dateFrom: string;
  dateTo: string;
  consumptionType: number;
}

const PeakConsumption: React.FC<PeakConsumptionProps> = ({ 
  dateFrom, 
  dateTo, 
  consumptionType
}) => {
  const [peakHours, setPeakHours] = React.useState<PeakData | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const theme = useTheme();

  // Theme-aware colors for dark mode compatibility
  const getProgressBarColors = () => {
    if (theme.palette.mode === 'dark') {
      return {
        background: theme.palette.grey[700],
        border: theme.palette.divider,
        paper: theme.palette.background.paper,
        blue: theme.palette.primary.main,
        brown: '#8B4513', // Same brown color in both modes
        green: theme.palette.success.main,
        red: theme.palette.error.main,
        grey: theme.palette.grey[600]
      };
    } else {
      return {
        background: '#e0e0e0',
        border: '#e0e0e0',
        paper: '#ffffff',
        blue: '#2196f3',
        brown: '#8B4513',
        green: '#4caf50',
        red: '#f44336',
        grey: '#9e9e9e'
      };
    }
  };

  const colors = getProgressBarColors();

  // Generate comparison message client-side based on percentage_value and typical_range
  const generateComparisonMessage = (percentageValue: number, typicalRange: { low: number; high: number }) => {
    if (percentageValue < typicalRange.low) {
      return  `less than a typical UK household.`;
    } else if (percentageValue <= typicalRange.high) {
      return `typical for a UK household.`;
    } else {
      return `more than a typical UK household.`;
    }
  };

  React.useEffect(() => {
    let isCancelled = false;
    
    const fetchPeakData = async () => {
      setError(null);
      
      try {
        
        // Check if we have a valid token
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No authentication token found");
        }
        
        // Create params for cache key
        const params = new URLSearchParams({
          type: consumptionType.toString(),
          date_from: dateFrom,
          date_to: dateTo
        });
        
        // Check cache first
        const { keyBase, prefix } = createCacheKeyFromParams('userconsumption-peak-hours-percentage', params);
        const cacheKey = getDailyCacheKey(keyBase, 6);
        const cachedData = getCachedData<PeakData>(cacheKey);

        if (cachedData) {
          // Use cached data
          if (!isCancelled) {
            setPeakHours(cachedData);
          }
        } else {
          // Cache miss - fetch from API
          const peakHoursData = await ApiClient.get(
            `api/userconsumption/peakHoursPercentage?type=${consumptionType}&date_from=${dateFrom}&date_to=${dateTo}`
          );

          if (!isCancelled) {
            // Store in cache
            setDailyCachedData(cacheKey, peakHoursData, prefix, 6);
            
            setPeakHours(peakHoursData);
          }
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Error fetching peak consumption data:', error);
          if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
            setError('Unable to connect to the server. Please check if the API is running.');
          } else {
            setError('Error fetching peak consumption data');
          }
        }
      }
    };

    fetchPeakData();
    
    return () => {
      isCancelled = true;
    };
  }, [dateFrom, dateTo, consumptionType]);

  if (error) {
    return (
      <Card sx={{ mb: 2, backgroundColor: theme.palette.background.paper }}>
        <CardContent>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Box sx={{ pt: 2 }}></Box>
      {peakHours && peakHours.percentage_value && (
      <Stack spacing={2}>
        <Typography variant="h3" sx={{ mb: 1 }}>
          Peak Hours Usage (4pm - 7pm)
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          
            <Box>            
              {/* Show original message if no percentage data available */}
              {(!peakHours.percentage_value || !peakHours.typical_range) && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {peakHours.message}
                </Typography>
              )}
              
              {/* Visual indicator for usage */}
              {peakHours.color_indicator && peakHours.percentage_value && (
                <Box sx={{ mb: 1 }}>
                  {/* Progress bar showing where user falls in the range */}
                  {peakHours.typical_range && (
                    <Box sx={{ 
                      width: '100%', 
                      mb: 1, 
                      border: 1, 
                      borderColor: colors.border, 
                      borderRadius: 1, 
                      p: 1.5,
                      pb: 4.0,
                      backgroundColor: colors.paper
                    }}>
                      {/* Percentage label inside the border */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1, 
                        mb: 1 
                      }}>
                        <Box sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: peakHours.color_indicator === 'green' ? colors.green :
                                          peakHours.color_indicator === 'red' ? colors.red : colors.grey
                        }} />
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {peakHours.percentage_value.toFixed(1)}% - {generateComparisonMessage(peakHours.percentage_value, peakHours.typical_range)}
                        </Typography>
                      </Box>
                      <ConsumptionProgressBar
                        percentageValue={peakHours.percentage_value}
                        usageCategory={peakHours.usage_category || 'typical'}
                        typicalRange={peakHours.typical_range}
                        width="100%"
                        typicalLowLimit={30}
                        typicalHighLimit={40}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
        </Box>
      </Stack>
    )}
    </>
  );
    
};

export default PeakConsumption;
